import { Dropdown, Spinner, Typography } from "@optimizely/axiom";
import React from "react";
import { IOrganizationProductInstance } from "../../../domain/OrganizationProduct";
import classnames from "classnames";
import { SEARCH_DROPDOWN_THRESHOLD } from "../../../constants/constants";
import { SearchInput } from "../SearchInput/SearchInput";

type InstanceDropdownProps = {
    className?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    instances: IOrganizationProductInstance[];
    loading?: boolean;
    multiselect?: boolean;
    onAllClick?: () => void;
    onChange: ({ value }: { value: IOrganizationProductInstance | IOrganizationProductInstance[] | null }) => void;
    placeholder?: string;
    value?: string | string[];
    white?: boolean;
    required?: boolean;
};
export const InstanceDropdown = ({
    className,
    disabled,
    fullWidth,
    instances,
    loading,
    multiselect = false,
    onAllClick,
    onChange,
    placeholder = "Select Instance...",
    value,
    white,
    required = false
}: InstanceDropdownProps) => {
    const [filterString, setFilterString] = React.useState<string>("");
    const instancesFromValue = instances?.filter((i) =>
        Array.isArray(value) ? value.indexOf(i.id) > -1 : i.id === value
    );

    const handleInstanceSelection = ({ instance }: { instance: IOrganizationProductInstance }) => {
        setFilterString("");
        if (multiselect) {
            const updated = [...instancesFromValue];
            const existingIndex = updated?.findIndex((i) => i.id === instance.id);
            if (existingIndex > -1) {
                updated.splice(existingIndex, 1);
            } else {
                updated.push(instance);
            }
            onChange({ value: updated });
        } else {
            onChange({ value: instance });
        }
    };

    const singleInstance = instancesFromValue[0];
    const noInstances = !instances?.length;
    let buttonContent: string | React.ReactNode = singleInstance?.nickname || singleInstance?.name || placeholder;
    if (loading) {
        buttonContent = (
            <>
                <Spinner size="small" /> Loading instances...
            </>
        );
    } else if (noInstances && !disabled) {
        buttonContent = "No instances available.";
    }

    const showFilter = instances.length > SEARCH_DROPDOWN_THRESHOLD;

    const filteredInstances = instances
        .sort((a, b) => {
            return (a.nickname || a.name).localeCompare(b.nickname || b.name);
        })
        .filter((i) => {
            return (i.nickname || i.name).toLowerCase().includes(filterString.toLowerCase());
        });

    return (
        <div className={className}>
            <Typography type="body" className="oui-label">
                {multiselect ? "Instances" : "Instance"}
                {required && <span aria-label="(required)" className="oui-label--required" />}
            </Typography>
            <Dropdown
                arrowIcon="down"
                onClick={(val: any) => {
                    setFilterString("");
                }}
                buttonContent={buttonContent}
                data-cy="instance-dropdown"
                className={classnames(className, "text--left", white && "admin-center__form-dropdown")}
                fullWidth={fullWidth}
                isDisabled={disabled || noInstances}
                style={white ? "outline" : undefined}
                whiteBackground={white}
            >
                <Dropdown.Contents style={{ width: "100%" }}>
                    {!!onAllClick && (
                        <Dropdown.ListItem role="option">
                            <Dropdown.BlockLink isLink onClick={onAllClick}>
                                <Dropdown.BlockLinkText text="All" />
                            </Dropdown.BlockLink>
                        </Dropdown.ListItem>
                    )}
                    {showFilter && (
                        <Dropdown.ListItem ignoreToggle>
                            <SearchInput
                                placeholder="Search instances..."
                                type="text"
                                onChange={({ value }) => {
                                    setFilterString(value);
                                }}
                                value={filterString}
                            />
                        </Dropdown.ListItem>
                    )}
                    {!filteredInstances?.length && filterString.length && (
                        <Dropdown.ListItem onClick={() => setFilterString("")}>
                            <Typography type="body" className="push--sides">
                                No results matching your search
                            </Typography>
                        </Dropdown.ListItem>
                    )}
                    {filteredInstances?.map((productInstance, index) => {
                        const { id: instanceId, name, nickname } = productInstance;
                        const inSelected = instancesFromValue?.find((i) => i.id === instanceId);
                        return (
                            <Dropdown.ListItem key={instanceId} role="option">
                                <Dropdown.BlockLink
                                    isLink={!inSelected}
                                    isMultiSelect={multiselect}
                                    isItemSelected={!!inSelected}
                                    onClick={() => handleInstanceSelection({ instance: productInstance })}
                                >
                                    <Dropdown.BlockLinkText text={nickname || name} />
                                </Dropdown.BlockLink>
                            </Dropdown.ListItem>
                        );
                    })}
                </Dropdown.Contents>
            </Dropdown>
        </div>
    );
};

InstanceDropdown.displayName = "InstanceDropdown";
